/**
 * Durable Volcano step persist — Designer and chat must write numbered steps,
 * never a chat transcript. Mirrors desk-guardrails-pack persistDesignerChange.
 */

export type DurableStep = {
  name: string;
  provider: string;
  version?: string;
  step_number?: number;
  step_template_id?: string;
  [key: string]: unknown;
};

export type PersistResult =
  | {
      ok: true;
      error: null;
      steps: DurableStep[];
      workflow_name: string;
      patch: { volcano_config: Record<string, unknown> };
      mergedAgent: Record<string, unknown>;
    }
  | {
      ok: false;
      error: 'designer_persist_not_a_step';
      message: string;
    };

export function isDurableStep(step: unknown): step is DurableStep {
  if (!step || typeof step !== 'object' || Array.isArray(step)) return false;
  const rec = step as Record<string, unknown>;
  const name = String(rec.name || '').trim();
  const provider = String(rec.provider || '').trim();
  if (!name || !provider) return false;
  if (/transcript|chat_message|assistant_message/i.test(name)) return false;
  return true;
}

export function stampDurableSteps(steps: DurableStep[]): DurableStep[] {
  return steps.map((step, index) => ({
    ...step,
    name: String(step.name || `step_${index + 1}`).trim(),
    step_number: index + 1,
    version: step.version || '1.0.0',
  }));
}

export function extractProposedSteps(proposed: unknown): unknown[] | null {
  if (Array.isArray(proposed)) return proposed;
  if (!proposed || typeof proposed !== 'object') return null;
  const rec = proposed as Record<string, unknown>;
  if (Array.isArray(rec.steps)) return rec.steps;
  const volcanoWorkflow = rec.volcano_workflow as { steps?: unknown[] } | undefined;
  if (Array.isArray(volcanoWorkflow?.steps)) return volcanoWorkflow.steps;
  const volcanoConfig = rec.volcano_config as { workflows?: Record<string, { steps?: unknown[] }> } | undefined;
  const workflows = volcanoConfig?.workflows || (rec.workflows as Record<string, { steps?: unknown[] }>);
  if (workflows && typeof workflows === 'object') {
    const first = workflows.default || Object.values(workflows)[0];
    if (first && Array.isArray(first.steps)) return first.steps;
  }
  if (typeof rec.explanation === 'string' && !rec.steps) return null;
  if (typeof rec.content === 'string' && !rec.provider) return null;
  return null;
}

export function persistDurableWorkflowChange({
  proposed,
  agent = null,
  workflowName = null,
}: {
  proposed: unknown;
  agent?: Record<string, unknown> | null;
  workflowName?: string | null;
}): PersistResult {
  const steps = extractProposedSteps(proposed);
  if (!steps) {
    return {
      ok: false,
      error: 'designer_persist_not_a_step',
      message: 'Designer cannot persist a change that is not a durable Volcano step graph.',
    };
  }
  if (!steps.length) {
    return {
      ok: false,
      error: 'designer_persist_not_a_step',
      message: 'Empty step list is not a durable workflow.',
    };
  }
  for (let i = 0; i < steps.length; i += 1) {
    if (!isDurableStep(steps[i])) {
      return {
        ok: false,
        error: 'designer_persist_not_a_step',
        message: `Item ${i + 1} is not a named/versioned Volcano step.`,
      };
    }
  }

  const stamped = stampDurableSteps(steps as DurableStep[]);
  const volcanoConfig = (agent?.volcano_config || {}) as {
    default_workflow?: string;
    workflows?: Record<string, Record<string, unknown>>;
  };
  const selected =
    workflowName ||
    (proposed as { workflow_name?: string } | null)?.workflow_name ||
    volcanoConfig.default_workflow ||
    'default';
  const existingWorkflow = volcanoConfig.workflows?.[selected] || {};
  const mergedAgent = {
    ...(agent || {}),
    volcano_config: {
      ...volcanoConfig,
      default_workflow: volcanoConfig.default_workflow || selected,
      workflows: {
        ...(volcanoConfig.workflows || {}),
        [selected]: {
          ...existingWorkflow,
          name: existingWorkflow.name || selected,
          steps: stamped,
        },
      },
    },
  };

  return {
    ok: true,
    error: null,
    steps: stamped,
    workflow_name: selected,
    patch: { volcano_config: mergedAgent.volcano_config },
    mergedAgent,
  };
}
